import {
  ApplicationConfig,
  DOCUMENT,
  ErrorHandler,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  PreloadAllModules,
  TitleStrategy,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { providePrimeNG } from 'primeng/config';
import { MessageService, ConfirmationService } from 'primeng/api';
import { definePreset } from '@primeuix/styled';
import Aura from '@primeuix/themes/aura';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { progressInterceptor } from '@core/interceptors/progress.interceptor';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { AuthService } from '@core/services/auth.service';
import { aplicarEquipoDeLaUrl } from '@core/services/equipo.service';
import { EntornoService } from '@core/entorno/entorno.service';
import { EntornoTitleStrategy } from '@core/entorno/entorno-title.strategy';
import { aplicarFaviconEntorno } from '@core/entorno/favicon-entorno';
import { ChunkPerdidoErrorHandler } from '@core/version/chunk-perdido.error-handler';
import { VersionDesplegadaService } from '@core/version/version-desplegada.service';
import { PantallaAnteriorService } from '@core/services/pantalla-anterior.service';
import { API_BASE_URL } from '@core/config/api.config';
import { BASE_PATH } from '@core/api/generated/variables';
import { environment } from '@env/environment';
import { tcgPresetTokens } from './tcg-preset.tokens';

// Registro global de módulos AG Grid. Community + Enterprise para evaluación.
// La license key se inyectará vía environment cuando se compre; sin ella, AG Grid
// pinta un watermark "AG Grid Enterprise Trial" en desarrollo (no en producción).
ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

// Datos de locale español: hace que DecimalPipe/CurrencyPipe (y por tanto las
// rejillas) formateen los miles con punto y los decimales con coma.
registerLocaleData(localeEs);

const TcgPreset = definePreset(Aura, tcgPresetTokens);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withPreloading(PreloadAllModules)),
    provideHttpClient(
      withInterceptors([authInterceptor, progressInterceptor, errorInterceptor]),
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: TcgPreset,
        options: { darkModeSelector: '.dark-mode', cssLayer: false },
      },
      ripple: true,
      // Calendario (y demás componentes) en español y formato España.
      translation: {
        firstDayOfWeek: 1,
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        monthNames: [
          'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
        ],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        today: 'Hoy',
        clear: 'Limpiar',
        dateFormat: 'dd/mm/yy',
        weekHeader: 'Sm',
      },
    }),
    // Locale por defecto de la app: formato español de números y fechas.
    { provide: LOCALE_ID, useValue: 'es-ES' },
    // Servicios PrimeNG globales (toast, dialogs de confirmación)
    MessageService,
    ConfirmationService,
    // Restaura la sesión y ESPERA a sus permisos (`GET api/auth/mis-permisos`) antes de que el router
    // evalúe los guards y se pinte el menú. Si la API falla, sigue con los permisos guardados.
    provideAppInitializer(() => firstValueFrom(inject(AuthService).restoreSession(), { defaultValue: undefined })),
    // El equipo del navegador: el acceso directo de cada PC de packing abre el ERP con `?equipo=%COMPUTERNAME%`.
    // Se guarda y se quita de la barra antes de la primera navegación. Ver `core/services/equipo.service.ts`.
    provideAppInitializer(aplicarEquipoDeLaUrl),
    // Marca del entorno: el título de cada pantalla lo prefija la estrategia; el favicon se tiñe una
    // vez, antes del primer pintado, para que no haya un instante de app sin marca. Ver `core/entorno/`.
    { provide: TitleStrategy, useClass: EntornoTitleStrategy },
    provideAppInitializer(() => {
      aplicarFaviconEntorno(inject(DOCUMENT), inject(EntornoService).descriptor());
    }),
    // URL base para el cliente OpenAPI generado ('' → rutas relativas → proxy en dev)
    { provide: BASE_PATH, useValue: environment.apiBaseUrl },
    { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
    // Rescata a quien se queda sin pantalla porque un despliegue le ha borrado un chunk por debajo.
    // No se traga nada: delega siempre en el manejador de Angular. Ver `core/version/`.
    { provide: ErrorHandler, useClass: ChunkPerdidoErrorHandler },
    // El servicio se instancia al arrancar para que la comprobación empiece sin esperar a que alguien
    // pinte la franja: si nadie la inyecta, nadie comprueba nada.
    // Servicios que tienen que estar escuchando al router desde la primera navegación: el de la
    // versión desplegada y el de la pantalla anterior (la flecha «volver» de Info producto vuelve a la
    // pantalla de la que se vino). Inyectarlos aquí es lo que los instancia.
    provideAppInitializer(() => {
      inject(VersionDesplegadaService);
      inject(PantallaAnteriorService);
    }),
  ],
};
